import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AlertCircle, Check, Copy, KeyRound, Loader2, Plus, Trash2 } from 'lucide-react';
import { apiKeysApi, type ApiKeySummary, type CreatedApiKey } from '@/api/apiKeys';

/**
 * Clés API — the operator surface for the public, machine-facing API.
 *
 * It manages credentials for a DIFFERENT surface: these keys authenticate
 * `/v1/public/*` calls, which are billed against the key owner's credits. So the
 * section is built around the three things an operator actually needs, and
 * nothing else:
 *
 *  1. **Create** a key, choosing its scopes. `read` lists models; `write` is what
 *     spends credits. A key that only reads cannot start a billed conversation,
 *     which is exactly why the choice is made here and not assumed.
 *  2. **See** what exists — prefix, name, scopes, WHOSE credits it spends, and
 *     when it was last used — so a key nobody remembers can be recognised.
 *  3. **Revoke** one, behind a confirmation, because revoking is instant and
 *     irreversible for every integration holding that key.
 *
 * ── THE SECRET IS SHOWN ONCE, AND THE SCREEN SAYS SO ────────────────────────
 * The server stores a SHA-256 of the key and nothing else. There is no endpoint
 * that can return the plaintext again — not for support, not for the owner. A UI
 * that showed the value without saying that would be setting a trap: the operator
 * closes the panel, comes back for the key, and finds a mask. So the reveal panel
 * states it in the same breath as it shows the value, keeps the value on screen
 * until it is DISMISSED (never on a timer, never on a re-render), and the list
 * below shows only the masked form for the rest of time.
 *
 * ── LAST USED IS COARSE, AND SAYS SO ────────────────────────────────────────
 * `lastUsedAt` is not written on every call — an API's throughput would make that
 * one database write per request. It is a "recently used" signal, and the copy
 * says that rather than implying an audit trail this column cannot provide.
 *
 * DA: aikka square brand, 5px radii, Signal Blue in support (`--signal-blue`),
 * no orange fill — in the platform, orange belongs to the customer's data
 * (DESIGN.md §1). Light and dark both come from the tokens, never a literal.
 */

/** The scopes the backend validates against. Kept in sync with `API_KEY_SCOPES`. */
const SCOPES = ['read', 'write'] as const;
type Scope = (typeof SCOPES)[number];

const KEYS_QUERY_KEY = ['api-keys'] as const;

export function SettingsApiKeysSection() {
  const { t } = useTranslation('os');
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<Scope[]>(['read', 'write']);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  /**
   * Les deux conditions d'une création, nommées UNE fois : le garde de
   * soumission et l'état désactivé du bouton les lisaient séparément, et le
   * garde en avait déjà oublié une.
   */
  const canCreate = name.trim().length > 0 && scopes.length > 0;

  const { data, isLoading, isError } = useQuery({
    queryKey: KEYS_QUERY_KEY,
    queryFn: () => apiKeysApi.list().then((r) => r.data),
  });

  const create = useMutation({
    mutationFn: () => apiKeysApi.create({ name: name.trim(), scopes }).then((r) => r.data),
    onSuccess: async (key) => {
      // The ONLY moment this value exists outside the server. Held in state and
      // dismissed by the operator — not on a timer, which would take a secret
      // away mid-copy.
      setCreated(key);
      setName('');
      await queryClient.invalidateQueries({ queryKey: KEYS_QUERY_KEY });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: async () => {
      setConfirmingId(null);
      await queryClient.invalidateQueries({ queryKey: KEYS_QUERY_KEY });
    },
  });

  const keys = data ?? [];
  // The one refusal worth naming precisely: the plan does not include API access.
  // Anything else is a generic failure — we do not guess at a cause.
  const createError = create.isError
    ? axios.isAxiosError(create.error) && create.error.response?.status === 400
      ? t('settings.apiKeys.createPlanError')
      : t('settings.apiKeys.createError')
    : null;

  return (
    <div className="space-y-6" data-testid="settings-api-keys">
      <div>
        <p className="text-sm text-foreground">{t('settings.apiKeys.intro')}</p>
        <p className="mt-1 text-xs text-ink-2">{t('settings.apiKeys.introDetail')}</p>
      </div>

      {/* ── The reveal. Only rendered after a creation, dismissed by hand. ─── */}
      {created && <RevealedKey created={created} onDismiss={() => setCreated(null)} />}

      {/* ── Create ────────────────────────────────────────────────────────── */}
      <form
        data-testid="api-key-create-form"
        className="space-y-3 rounded-control border border-border bg-surface-1 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canCreate && !create.isPending) create.mutate();
        }}
      >
        {/* LE NOM DEVIENT OBLIGATOIRE (Notion → Settings → API Key :
            « make "name" mandatory »).

            Il était facultatif, et la liste des clés portait donc une entrée
            « Sans nom » — parfois plusieurs. Or une clé d'API ne se distingue
            que par son nom : le secret n'est montré qu'une fois, il ne reste
            que le préfixe et la date. Devant trois clés « Sans nom », révoquer
            la bonne relève du pari, et c'est le geste où se tromper coûte le
            plus cher.

            Obligatoire côté CLIENT : le serveur accepte toujours une création
            sans nom (les clés déjà créées ainsi restent valides et la
            compatibilité de `POST /v1/keys` n'est pas cassée). C'est la porte
            qui se ferme, pas le contrat. */}
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-foreground">
            {t('settings.apiKeys.nameLabel')}
          </span>
          <input
            type="text"
            value={name}
            maxLength={64}
            required
            data-testid="api-key-name"
            onChange={(e) => setName(e.target.value)}
            placeholder={t('settings.apiKeys.namePlaceholder')}
            className="w-full rounded-control border border-border bg-surface px-2.5 py-1.5 text-sm text-foreground outline-none transition-colors placeholder:text-ink-2 focus-visible:border-[color:var(--signal-blue)] focus-visible:ring-1 focus-visible:ring-[color:var(--signal-blue)]"
          />
        </label>

        <fieldset>
          <legend className="mb-1 block text-xs font-medium text-foreground">
            {t('settings.apiKeys.scopesLabel')}
          </legend>
          <div className="flex flex-wrap gap-2">
            {SCOPES.map((scope) => {
              const on = scopes.includes(scope);
              return (
                <label
                  key={scope}
                  data-testid={`api-key-scope-${scope}`}
                  className={`inline-flex cursor-pointer items-center gap-2 rounded-control border px-2.5 py-1.5 text-xs transition-colors ${
                    on
                      ? 'border-[color:var(--signal-blue)] bg-[color:var(--signal-blue)]/10 text-foreground'
                      : 'border-border text-ink-2 hover:bg-secondary/60'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={on}
                    onChange={() =>
                      setScopes((prev) =>
                        prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
                      )
                    }
                  />
                  <span className="font-data uppercase tracking-[0.08em]">{scope}</span>
                  <span className="text-ink-2">{t(`settings.apiKeys.scope.${scope}`)}</span>
                </label>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-ink-2">{t('settings.apiKeys.scopesHint')}</p>
        </fieldset>

        {name.trim().length === 0 && (
          <p role="alert" data-testid="api-key-no-name" className="text-xs text-foreground">
            {t('settings.apiKeys.noName')}
          </p>
        )}
        {scopes.length === 0 && (
          <p role="alert" data-testid="api-key-no-scope" className="text-xs text-foreground">
            {t('settings.apiKeys.noScope')}
          </p>
        )}
        {createError && (
          <p role="alert" data-testid="api-key-create-error" className="flex items-center gap-1.5 text-xs text-foreground">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 text-[color:var(--signal-blue)]" aria-hidden="true" />
            {createError}
          </p>
        )}

        <button
          type="submit"
          data-testid="api-key-create"
          disabled={!canCreate || create.isPending}
          className="inline-flex items-center gap-1.5 rounded-control border border-[color:var(--signal-blue)] bg-[color:var(--signal-blue)]/10 px-3 py-1.5 text-xs font-medium text-foreground outline-none transition-colors hover:bg-[color:var(--signal-blue)]/20 focus-visible:ring-1 focus-visible:ring-[color:var(--signal-blue)] disabled:opacity-60"
        >
          {create.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {t('settings.apiKeys.create')}
        </button>
      </form>

      {/* ── List ──────────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <h3 className="font-data text-[9px] uppercase tracking-[0.2em] text-ink-2">
          {t('settings.apiKeys.listTitle')}
        </h3>

        {isLoading ? (
          <p className="text-sm text-ink-2" data-testid="api-keys-loading">
            {t('settings.apiKeys.loading')}
          </p>
        ) : isError ? (
          <p role="alert" data-testid="api-keys-error" className="text-sm text-foreground">
            {t('settings.apiKeys.loadError')}
          </p>
        ) : keys.length === 0 ? (
          <p className="text-sm text-ink-2" data-testid="api-keys-empty">
            {t('settings.apiKeys.empty')}
          </p>
        ) : (
          <ul className="space-y-2">
            {keys.map((key) => (
              <KeyRow
                key={key.id}
                apiKey={key}
                confirming={confirmingId === key.id}
                pending={revoke.isPending && confirmingId === key.id}
                onAskRevoke={() => setConfirmingId(key.id)}
                onCancel={() => setConfirmingId(null)}
                onConfirm={() => revoke.mutate(key.id)}
              />
            ))}
          </ul>
        )}

        {revoke.isError && (
          <p role="alert" data-testid="api-key-revoke-error" className="text-xs text-foreground">
            {t('settings.apiKeys.revokeError')}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * The one-time reveal.
 *
 * Deliberately NOT a toast and NOT on a timer: a secret that vanishes while the
 * operator is switching to their password manager is a secret they have to
 * revoke and re-create. It stays until dismissed, and the dismissal is the only
 * way it leaves.
 */
function RevealedKey({ created, onDismiss }: { created: CreatedApiKey; onDismiss: () => void }) {
  const { t } = useTranslation('os');
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied(true);
    } catch {
      // No clipboard permission (or no clipboard at all). The value is on screen
      // and selectable, so the operator is not stuck — we just do not claim a
      // copy that did not happen.
      setCopied(false);
    }
  };

  return (
    <div
      role="alert"
      data-testid="api-key-revealed"
      className="space-y-2 rounded-control border border-[color:var(--signal-blue)] bg-[color:var(--signal-blue)]/5 p-4"
    >
      <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <KeyRound className="h-4 w-4 shrink-0 text-[color:var(--signal-blue)]" aria-hidden="true" />
        {t('settings.apiKeys.revealTitle')}
      </p>
      {/* The warning sits ABOVE the value, not below it: it has to be read before
          the panel is closed, not after. */}
      <p className="text-xs text-foreground" data-testid="api-key-once-warning">
        {t('settings.apiKeys.revealOnce')}
      </p>
      <div className="flex items-center gap-2">
        <code
          data-testid="api-key-value"
          className="min-w-0 flex-1 overflow-x-auto rounded-control border border-border bg-surface px-2.5 py-1.5 font-data text-xs text-foreground"
        >
          {created.key}
        </code>
        <button
          type="button"
          data-testid="api-key-copy"
          onClick={() => void copy()}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-control border border-border px-2.5 py-1.5 text-xs text-ink-2 outline-none transition-colors hover:bg-secondary hover:text-foreground focus-visible:ring-1 focus-visible:ring-[color:var(--signal-blue)]"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {copied ? t('settings.apiKeys.copied') : t('settings.apiKeys.copy')}
        </button>
      </div>
      <button
        type="button"
        data-testid="api-key-dismiss"
        onClick={onDismiss}
        className="rounded-control px-1 text-xs text-ink-2 underline underline-offset-2 outline-none hover:text-foreground focus-visible:ring-1 focus-visible:ring-[color:var(--signal-blue)]"
      >
        {t('settings.apiKeys.dismiss')}
      </button>
    </div>
  );
}

function KeyRow({
  apiKey,
  confirming,
  pending,
  onAskRevoke,
  onCancel,
  onConfirm,
}: {
  apiKey: ApiKeySummary;
  confirming: boolean;
  pending: boolean;
  onAskRevoke: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // `i18n` for the same reason `t` is here: the "last used" line ends in a date,
  // and a date is interface text too — see {@link formatDate} at the bottom.
  const { t, i18n } = useTranslation('os');

  return (
    <li
      data-testid={`api-key-row-${apiKey.id}`}
      className="rounded-control border border-border bg-surface-1 px-3 py-2.5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm text-foreground">
            {apiKey.name || t('settings.apiKeys.unnamed')}
          </p>
          <p className="mt-0.5 font-data text-xs text-ink-2" data-testid={`api-key-prefix-${apiKey.id}`}>
            {apiKey.keyPrefix}
            <span aria-hidden="true">{'…'}</span>
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-2">
            <span className="font-data uppercase tracking-[0.08em]">{apiKey.scopes.join(' · ')}</span>
            <span aria-hidden="true">·</span>
            {/* Coarse by design — the copy says "recently", not a timestamp. */}
            <span data-testid={`api-key-last-used-${apiKey.id}`}>
              {apiKey.lastUsedAt
                ? t('settings.apiKeys.lastUsed', { date: formatDate(apiKey.lastUsedAt, i18n.language) })
                : t('settings.apiKeys.neverUsed')}
            </span>
            {apiKey.ownerEmail && (
              <>
                <span aria-hidden="true">·</span>
                {/* Whose credits this key spends. On a multi-member account this
                    is the difference between an informed revocation and a guess. */}
                <span data-testid={`api-key-owner-${apiKey.id}`}>
                  {t('settings.apiKeys.owner', { owner: apiKey.ownerName || apiKey.ownerEmail })}
                </span>
              </>
            )}
          </p>
        </div>

        {confirming ? (
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              data-testid={`api-key-revoke-confirm-${apiKey.id}`}
              disabled={pending}
              onClick={onConfirm}
              className="inline-flex items-center gap-1.5 rounded-control border border-[color:var(--signal-blue)] bg-[color:var(--signal-blue)]/10 px-2.5 py-1 text-xs text-foreground outline-none transition-colors hover:bg-[color:var(--signal-blue)]/20 focus-visible:ring-1 focus-visible:ring-[color:var(--signal-blue)] disabled:opacity-60"
            >
              {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              {t('settings.apiKeys.revokeConfirm')}
            </button>
            <button
              type="button"
              data-testid={`api-key-revoke-cancel-${apiKey.id}`}
              onClick={onCancel}
              className="rounded-control px-2 py-1 text-xs text-ink-2 outline-none transition-colors hover:text-foreground focus-visible:ring-1 focus-visible:ring-[color:var(--signal-blue)]"
            >
              {t('settings.apiKeys.cancel')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid={`api-key-revoke-${apiKey.id}`}
            onClick={onAskRevoke}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-control border border-border px-2.5 py-1 text-xs text-ink-2 outline-none transition-colors hover:bg-secondary hover:text-foreground focus-visible:ring-1 focus-visible:ring-[color:var(--signal-blue)]"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            {t('settings.apiKeys.revoke')}
          </button>
        )}
      </div>

      {confirming && (
        <p className="mt-2 text-xs text-foreground" data-testid={`api-key-revoke-warning-${apiKey.id}`}>
          {t('settings.apiKeys.revokeWarning')}
        </p>
      )}
    </li>
  );
}

/**
 * The "last used" date, in the language the screen is being read in.
 *
 * It used to pass `undefined` as the locale, which is not "no opinion": it is
 * the HOST's opinion — the browser's, or Node's in a test run. So an operator
 * reading this panel in Spanish saw "20 Aug 2026" under a Spanish sentence,
 * for the same reason `lib/pricing.ts` printed "1 Dec 2026" under a French one
 * a screen away. A month name is interface text no catalogue holds, so neither
 * `i18n-audit --strict` nor the `i18n-hardcoded` ledger can go red on it; only
 * mounting the screen in another locale can, which is what
 * `settings-api-keys-section.test.tsx` now does.
 *
 * The invalid-date branch stays: `lastUsedAt` comes from the API, and echoing
 * the raw value beats printing "Invalid Date" at an operator.
 *
 * @example formatDate(iso, 'fr') // "20 août 2026"
 * @example formatDate(iso, 'es') // "20 ago 2026"
 */
function formatDate(iso: string, locale?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const options = { day: '2-digit', month: 'short', year: 'numeric' } as const;
  try {
    return date.toLocaleDateString(locale || undefined, options);
  } catch {
    // An unsupported tag throws RangeError; the host default still beats a crash.
    return date.toLocaleDateString(undefined, options);
  }
}
